import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, Table, TableHead, TableRow, TableCell, TableBody,
  Button, Chip, CircularProgress, TextField, InputAdornment, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid, MenuItem, IconButton,
  Tooltip, TablePagination, Alert, Divider, Tabs, Tab,
  ToggleButtonGroup, ToggleButton
} from '@mui/material';
import { Add, Search, Refresh, Edit, Delete, PriceChange, ArrowBack, Warning,
  People, PhoneForwarded, ListAlt, Upload, CheckCircle } from '@mui/icons-material';
import LinearProgress from '@mui/material/LinearProgress';
import api from '../../services/api';

const emptyPlan = { name: '', description: '', plan_type: 'retail', currency: 'USD', billing_increment: 6, min_duration: 0, connect_fee: 0, status: 'active' };
const emptyRate: any = {
  destination: '', destination_name: '', destination_group: '', carrier_id: '',
  rate_peak: '', rate_offpeak: '', buy_rate: '', buy_rate_offpeak: '',
  connect_fee: 0, billing_increment: 6, min_duration: 0,
  effective_date: new Date().toISOString().slice(0, 10),
};

export const RatesPage: React.FC = () => {
  const [view, setView] = useState<'plans' | 'rates'>('plans');
  const [plans, setPlans] = useState<any[]>([]);
  const [totalPlans, setTotalPlans] = useState(0);
  const [rates, setRates] = useState<any[]>([]);
  const [totalRates, setTotalRates] = useState(0);
  const [loading, setLoading] = useState(true);
  const [planSearch, setPlanSearch] = useState('');
  const [rateSearch, setRateSearch] = useState('');
  const [page, setPage] = useState(0);
  const [planPage, setPlanPage] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [planDialog, setPlanDialog] = useState(false);
  const [rateDialog, setRateDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState<any>(null);
  const [editingRate, setEditingRate] = useState<any>(null);
  const [planForm, setPlanForm] = useState<any>(emptyPlan);
  const [rateForm, setRateForm] = useState<any>(emptyRate);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [carriers, setCarriers] = useState<any[]>([]);
  const [planTypeTab, setPlanTypeTab] = useState<'all' | 'retail' | 'wholesale' | 'reseller'>('all');
  const [csvDialog, setCsvDialog] = useState(false);
  const [csvRows, setCsvRows] = useState<any[]>([]);
  const [csvFileName, setCsvFileName] = useState('');
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvResult, setCsvResult] = useState<{created:number;updated:number;errors:string[]} | null>(null);
  const [csvMapping, setCsvMapping] = useState<Record<string, string>>({});
  const [csvWarnings, setCsvWarnings] = useState<string[]>([]);
  const [csvImportMode, setCsvImportMode] = useState<'auto'|'termination'>('auto');

  // ── CSV helpers ──────────────────────────────────────────────────────────

  // Detect delimiter from first line
  const detectDelimiter = (firstLine: string): string => {
    const counts: Record<string, number> = { ',': 0, '\t': 0, ';': 0, '|': 0 };
    for (const ch of firstLine) if (ch in counts) counts[ch]++;
    return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
  };

  // Proper quoted-field CSV splitter
  const splitLine = (line: string, delim: string): string[] => {
    const cols: string[] = [];
    let cur = ''; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { inQ = !inQ; continue; }
      if (!inQ && line.slice(i, i + delim.length) === delim) { cols.push(cur.trim()); cur = ''; i += delim.length - 1; continue; }
      cur += ch;
    }
    cols.push(cur.trim());
    return cols;
  };

  const parseCSV = (raw: string, importMode: 'auto'|'termination'): { rows: any[]; mapping: Record<string, string>; warnings: string[] } => {
    const text = raw.replace(/^﻿/, '');
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) return { rows: [], mapping: {}, warnings: ['File has no data rows'] };

    const delim = detectDelimiter(lines[0]);
    const rawHeaders = splitLine(lines[0], delim);
    const headers = rawHeaders.map(h => h.toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, ''));

    const colIdx = (names: string[]) => {
      for (const n of names) { const i = headers.indexOf(n); if (i >= 0) return i; }
      for (const n of names) { const i = headers.findIndex(h => h.includes(n) || n.includes(h)); if (i >= 0) return i; }
      return -1;
    };

    // ── Destination: prefer dialcode/code/prefix over generic "destination"
    // (ASTPP termination CSV: "Code" = prefix, "Destination" = description text)
    const iDestStrict = colIdx(['dialcode','dial_code','code','prefix','e164','number']);
    const iDestFallback = colIdx(['destination','dest']);
    const iDest = iDestStrict >= 0 ? iDestStrict : iDestFallback;

    // ── Name: if "code" was used for destination, "destination" becomes the name
    const iNameCandidates = ['destination_name','ratename','rate_name','description','name','country','dest_name','dial_code_description','dialcode_description'];
    if (iDestStrict >= 0 && iDestFallback >= 0) iNameCandidates.unshift('destination');
    const iName = colIdx(iNameCandidates);

    // ── Sell rate (origination)
    const iSell = colIdx(['origination_rate','origination','orig_rate','sell_rate','rate_peak','peak_rate','sell','price','customer_rate']);

    // ── Buy rate (termination): "Cost/Min" → normalises to cost_min
    const iBuy = colIdx(['termination_rate','termination','term_rate','buy_rate','cost_min','cost_per_min','cost_per_minute','cost_minute','buy','buycost','carrier_rate','interconnect_rate','wholesale_rate']);

    // ── Generic "rate"/"cost" column — direction depends on importMode
    const iGenericRate = colIdx(['rate','cost']);

    // ── Other columns
    const iOffSell = colIdx(['offpeak_origination','origination_offpeak','off_peak_origination','rate_offpeak','off_peak_sell','offpeak','off_peak']);
    const iOffBuy  = colIdx(['offpeak_termination','termination_offpeak','off_peak_termination','buy_rate_offpeak','off_peak_buy','buy_offpeak']);
    const iConFee  = colIdx(['connectfee','connect_fee','connection_fee','setup_fee']);
    // ASTPP: "Initial Increment" = min billing block, "Increment" = step
    const iBillInc = colIdx(['increments','increment','billing_block','billing_increment','bill_inc','min_dur']);
    const iInitInc = colIdx(['initial_increment','initial_block','min_increment']);
    const iTrunk   = colIdx(['trunk','carrier','carrier_name','trunk_name']);

    // Resolve effective sell/buy indices based on mode
    const effectiveSell = importMode === 'termination' ? -1 : (iSell >= 0 ? iSell : (iBuy < 0 && iGenericRate >= 0 ? iGenericRate : -1));
    const effectiveBuy  = importMode === 'termination'
      ? (iBuy >= 0 ? iBuy : iSell >= 0 ? iSell : iGenericRate >= 0 ? iGenericRate : -1)
      : (iBuy >= 0 ? iBuy : (iSell < 0 && iGenericRate >= 0 ? iGenericRate : -1));

    const mapping: Record<string, string> = {};
    if (iDest       >= 0) mapping['Destination']      = rawHeaders[iDest];
    if (iName       >= 0) mapping['Name']             = rawHeaders[iName];
    if (effectiveSell>=0) mapping['Sell Rate (Orig)'] = rawHeaders[effectiveSell];
    if (effectiveBuy >= 0) mapping['Buy Rate (Term)'] = rawHeaders[effectiveBuy];
    if (iOffSell    >= 0) mapping['Off-Peak Sell']    = rawHeaders[iOffSell];
    if (iOffBuy     >= 0) mapping['Off-Peak Buy']     = rawHeaders[iOffBuy];
    if (iConFee     >= 0) mapping['Connect Fee']      = rawHeaders[iConFee];
    if (iBillInc    >= 0) mapping['Billing Inc.']     = rawHeaders[iBillInc];
    if (iInitInc    >= 0) mapping['Initial Increment']= rawHeaders[iInitInc];
    if (iTrunk      >= 0) mapping['Trunk/Carrier']    = rawHeaders[iTrunk];

    const warnings: string[] = [];
    if (iDest < 0) warnings.push('⚠ No destination/prefix column found — cannot import');
    if (importMode !== 'termination' && effectiveSell < 0) warnings.push('ℹ No origination/sell rate column — sell rates will not be updated');
    if (effectiveBuy < 0) warnings.push('⚠ No termination/buy rate column found — buy rates will not be set');
    if (iDest < 0) return { rows: [], mapping, warnings };

    const rows = lines.slice(1).map(line => {
      const cols = splitLine(line, delim);
      const dest = cols[iDest] || '';
      if (!dest || !/^\+?\d/.test(dest)) return null; // skip non-numeric
      const row: any = { destination: dest };
      if (iName        >= 0 && cols[iName])        row.destination_name  = cols[iName];
      if (effectiveSell>= 0 && cols[effectiveSell]) row.rate_peak        = cols[effectiveSell];
      if (effectiveBuy >= 0 && cols[effectiveBuy])  row.buy_rate         = cols[effectiveBuy];
      if (iOffSell     >= 0 && cols[iOffSell])      row.rate_offpeak     = cols[iOffSell];
      if (iOffBuy      >= 0 && cols[iOffBuy])       row.buy_rate_offpeak = cols[iOffBuy];
      if (iConFee      >= 0 && cols[iConFee])       row.connect_fee      = cols[iConFee];
      if (iBillInc     >= 0 && cols[iBillInc])      row.billing_increment= cols[iBillInc];
      // ASTPP carrier/trunk name stored for reference
      if (iTrunk       >= 0 && cols[iTrunk])        row._trunk           = cols[iTrunk];
      return row;
    }).filter(Boolean) as any[];

    return { rows, mapping, warnings };
  };

  const handleCsvFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFileName(file.name);
    setCsvResult(null);
    const reader = new FileReader();
    reader.onload = ev => {
      const { rows, mapping, warnings } = parseCSV(ev.target?.result as string, csvImportMode);
      setCsvRows(rows);
      setCsvMapping(mapping);
      setCsvWarnings(warnings);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const runCsvImport = async () => {
    if (!selectedPlan || csvRows.length === 0) return;
    setCsvImporting(true); setCsvResult(null);
    try {
      const { data } = await api.put(`/rates/plans/${selectedPlan.id}/rates/bulk-upsert`, csvRows);
      setCsvResult(data);
      loadRates();
    } catch (e: any) { setCsvResult({ created: 0, updated: 0, errors: [e.response?.data?.message || 'Import failed'] }); }
    finally { setCsvImporting(false); }
  };

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: '25', offset: String(planPage * 25) });
      if (planSearch) p.set('search', planSearch);
      if (planTypeTab !== 'all') p.set('plan_type', planTypeTab);
      const { data: res } = await api.get(`/rates/plans?${p}`);
      setPlans(res.data || (Array.isArray(res) ? res : []));
      setTotalPlans(res.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [planPage, planSearch, planTypeTab]);

  const loadRates = useCallback(async () => {
    if (!selectedPlan) return;
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: '50', offset: String(page * 50) });
      if (rateSearch) p.set('search', rateSearch);
      const { data: res } = await api.get(`/rates/plans/${selectedPlan.id}/rates?${p}`);
      setRates(res.data || (Array.isArray(res) ? res : []));
      setTotalRates(res.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [selectedPlan, page, rateSearch]);

  useEffect(() => { if (view === 'plans') loadPlans(); }, [view, loadPlans, planTypeTab]);
  useEffect(() => { if (view === 'rates') loadRates(); }, [view, loadRates]);
  useEffect(() => {
    api.get('/carriers?limit=200').then(({ data: res }) => {
      setCarriers(res.data || (Array.isArray(res) ? res : []));
    }).catch(() => {});
  }, []);

  const openPlan = (plan: any) => { setSelectedPlan(plan); setPage(0); setRateSearch(''); setView('rates'); };

  const savePlan = async () => {
    setSaving(true); setError('');
    try {
      const body = { ...planForm, did_monthly_fee: planForm.did_monthly_fee === '' || planForm.did_monthly_fee == null ? null : Number(planForm.did_monthly_fee) };
      if (editingPlan) await api.put(`/rates/plans/${editingPlan.id}`, body);
      else await api.post('/rates/plans', body);
      setPlanDialog(false); loadPlans();
    } catch (e: any) { setError(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const sellBuyMargin = () => {
    const sell = parseFloat(rateForm.rate_peak);
    const buy = parseFloat(rateForm.buy_rate);
    if (!isNaN(sell) && !isNaN(buy)) return ((sell - buy) / (buy || 1) * 100);
    return null;
  };

  const marginInvalid = () => {
    const sell = parseFloat(rateForm.rate_peak);
    const buy = parseFloat(rateForm.buy_rate);
    return !isNaN(sell) && !isNaN(buy) && sell < buy;
  };

  const saveRate = async () => {
    if (marginInvalid()) { setError('Sell rate cannot be lower than buy rate.'); return; }
    setSaving(true); setError('');
    try {
      const payload: any = {
        ...rateForm,
        carrier_id: rateForm.carrier_id || null,
        rate_peak: parseFloat(rateForm.rate_peak) || 0,
        rate_offpeak: rateForm.rate_offpeak !== '' ? parseFloat(rateForm.rate_offpeak) : null,
        buy_rate: parseFloat(rateForm.buy_rate) || 0,
        buy_rate_offpeak: rateForm.buy_rate_offpeak !== '' ? parseFloat(rateForm.buy_rate_offpeak) : null,
        connect_fee: parseFloat(rateForm.connect_fee) || 0,
      };
      if (editingRate) await api.put(`/rates/${editingRate.id}`, payload);
      else await api.post(`/rates/plans/${selectedPlan.id}/rates`, payload);
      setRateDialog(false); loadRates();
    } catch (e: any) { setError(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const margin = (sell: any, buy: any) => {
    const s = parseFloat(sell), b = parseFloat(buy);
    if (isNaN(s) || isNaN(b) || b === 0) return null;
    return ((s - b) / b * 100).toFixed(1);
  };

  const carrierName = (id: string) => carriers.find(c => c.id === id)?.name || null;

  const planTypeChip = (pt: string) => {
    switch (pt) {
      case 'retail': return <Chip icon={<People sx={{ fontSize:'14px !important' }}/>} label="Customer — Retail" size="small" color="primary" variant="outlined" />;
      case 'wholesale': return <Chip icon={<PhoneForwarded sx={{ fontSize:'14px !important' }}/>} label="Carrier — Termination" size="small" color="secondary" variant="outlined" />;
      case 'reseller': return <Chip icon={<ListAlt sx={{ fontSize:'14px !important' }}/>} label="Reseller" size="small" color="success" variant="outlined" />;
      default: return <Chip label={pt} size="small" variant="outlined" />;
    }
  };

  if (view === 'rates' && selectedPlan) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button startIcon={<ArrowBack />} onClick={() => setView('plans')} variant="outlined" size="small">Rate Plans</Button>
            <Box>
              <Typography variant="h5" fontWeight={700}>{selectedPlan.name}</Typography>
              <Box sx={{ display:'flex', alignItems:'center', gap:1, mt:0.5 }}>
                <Typography variant="body2" color="text.secondary">{totalRates} rates — {selectedPlan.currency}</Typography>
                {planTypeChip(selectedPlan.plan_type)}
                {selectedPlan.plan_type === 'wholesale' && <Chip label="Buy / Termination" size="small" color="secondary" />}
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField size="small" placeholder="Search rates..." value={rateSearch}
              onChange={e => { setRateSearch(e.target.value); setPage(0); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
              sx={{ width: 220 }} />
            <Button variant="outlined" startIcon={<Refresh />} onClick={loadRates}>Refresh</Button>
            <Button variant="outlined" startIcon={<Upload />} onClick={() => { setCsvRows([]); setCsvFileName(''); setCsvResult(null); setCsvMapping({}); setCsvWarnings([]); setCsvImportMode('auto'); setCsvDialog(true); }}>
              Import CSV
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={() => { setEditingRate(null); setRateForm({ ...emptyRate }); setError(''); setRateDialog(true); }}>
              Add Rate
            </Button>
          </Box>
        </Box>

        <Card>
          {loading ? <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box> : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {(selectedPlan?.plan_type === 'wholesale'
                    ? ['Destination Code', 'Destination Name', 'Carrier', 'Termination Rate', 'Off-Peak Rate', 'Billing Inc.', 'Actions']
                    : ['Destination Code', 'Destination Name', 'Trunk (Carrier)', 'Sell Rate', 'Buy Rate', 'Margin', 'Off-Peak Sell', 'Billing Inc.', 'Actions']
                  ).map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rates.map(row => {
                    const m = margin(row.rate_peak, row.buy_rate);
                    const isNegative = m !== null && parseFloat(m) < 0;
                    return (
                      <TableRow key={row.id} hover sx={isNegative ? { bgcolor: 'error.lighter' } : {}}>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{row.destination}</TableCell>
                        <TableCell>{row.destination_name || '—'}</TableCell>
                        <TableCell>
                          {row.carrier_id ? (
                            <Chip label={carrierName(row.carrier_id) || row.carrier_name || row.carrier_id.slice(0,8)} size="small" color="primary" variant="outlined" />
                          ) : <Typography variant="caption" color="text.disabled">Any / Profile</Typography>}
                        </TableCell>
                        {selectedPlan?.plan_type === 'wholesale' ? (
                          <>
                            <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'secondary.main' }}>{row.rate_peak == null ? '—' : Number(row.rate_peak).toFixed(4)}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{row.rate_offpeak ? Number(row.rate_offpeak).toFixed(4) : '—'}</TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{row.rate_peak == null ? '—' : Number(row.rate_peak).toFixed(4)}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', color: Number(row.buy_rate) > 0 ? 'text.primary' : 'text.disabled' }}>{Number(row.buy_rate) > 0 ? Number(row.buy_rate).toFixed(4) : '—'}</TableCell>
                            <TableCell>
                              {m !== null ? (
                                <Chip label={`${m}%`} size="small" color={isNegative ? 'error' : parseFloat(m) < 10 ? 'warning' : 'success'} />
                              ) : '—'}
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{row.rate_offpeak ? Number(row.rate_offpeak).toFixed(4) : '—'}</TableCell>
                          </>
                        )}
                        <TableCell>{row.billing_increment}s</TableCell>
                        <TableCell>
                          <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditingRate(row); setRateForm({ ...emptyRate, ...row, carrier_id: row.carrier_id || '', rate_peak: String(row.rate_peak), buy_rate: String(row.buy_rate), rate_offpeak: row.rate_offpeak != null ? String(row.rate_offpeak) : '', buy_rate_offpeak: row.buy_rate_offpeak != null ? String(row.buy_rate_offpeak) : '' }); setError(''); setRateDialog(true); }}><Edit fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Delete"><IconButton size="small" color="error" onClick={async () => { if (window.confirm('Delete this rate?')) { await api.delete(`/rates/${row.id}`); loadRates(); } }}><Delete fontSize="small" /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {rates.length === 0 && (
                    <TableRow><TableCell colSpan={9} align="center" sx={{ py: 6, color: 'text.secondary' }}>No rates yet. Add rates to define pricing and routing trunks per destination.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination component="div" count={totalRates} page={page} rowsPerPage={50}
                onPageChange={(_, p) => setPage(p)} rowsPerPageOptions={[50]} />
            </>
          )}
        </Card>

        {/* Rate Dialog */}
        <Dialog open={rateDialog} onClose={() => setRateDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>{editingRate ? 'Edit Rate' : `Add Rate — ${selectedPlan.name}`}</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Grid container spacing={2}>
              <Grid item xs={12}><Divider textAlign="left"><Typography variant="caption" color="text.secondary">DESTINATION</Typography></Divider></Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Destination Code *" value={rateForm.destination}
                  onChange={e => setRateForm({ ...rateForm, destination: e.target.value })}
                  placeholder="e.g. 1, 44, 966" helperText="E.164 prefix without + sign" required />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Destination Name" value={rateForm.destination_name}
                  onChange={e => setRateForm({ ...rateForm, destination_name: e.target.value })}
                  placeholder="e.g. USA, UK, Saudi Arabia Fixed" />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Group" value={rateForm.destination_group || ''}
                  onChange={e => setRateForm({ ...rateForm, destination_group: e.target.value })}
                  placeholder="e.g. NANP, Europe, Gulf" />
              </Grid>

              <Grid item xs={12}><Divider textAlign="left"><Typography variant="caption" color="text.secondary">TRUNK (ROUTING)</Typography></Divider></Grid>
              <Grid item xs={12}>
                <TextField select fullWidth label="Trunk / Carrier *" value={rateForm.carrier_id || ''}
                  onChange={async e => {
                    const cid = e.target.value;
                    setRateForm(prev => ({ ...prev, carrier_id: cid }));
                    // Auto-populate the buy rate from this trunk's rate for this destination
                    if (cid && rateForm.destination) {
                      try {
                        const { data } = await api.get('/rates/lookup/buy', { params: { carrier_id: cid, destination: rateForm.destination } });
                        if (data && data.buy_rate != null) {
                          setRateForm(prev => ({ ...prev, carrier_id: cid, buy_rate: String(data.buy_rate) }));
                        }
                      } catch { /* leave buy rate as-is if lookup fails */ }
                    }
                  }}
                  helperText="Selecting a trunk auto-fills its buy rate for this destination. Leave empty to use the customer routing profile.">
                  <MenuItem value=""><em>— Use routing profile (no fixed trunk) —</em></MenuItem>
                  {carriers.filter(c => c.status === 'active').map(c => (
                    <MenuItem key={c.id} value={c.id}>{c.name} — {c.host}:{c.port || 5060}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12}><Divider textAlign="left"><Typography variant="caption" color="text.secondary">RATES</Typography></Divider></Grid>
              <Grid item xs={12} md={3}>
                <TextField fullWidth label="Sell Rate (Peak) *" type="number" value={rateForm.rate_peak}
                  onChange={e => setRateForm({ ...rateForm, rate_peak: e.target.value })}
                  error={marginInvalid()}
                  InputProps={{ startAdornment: <InputAdornment position="start">{selectedPlan.currency}/min</InputAdornment> }}
                  inputProps={{ step: '0.0001' }} required />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField fullWidth label="Buy Rate (Peak)" type="number" value={rateForm.buy_rate}
                  onChange={e => setRateForm({ ...rateForm, buy_rate: e.target.value })}
                  InputProps={{ startAdornment: <InputAdornment position="start">{selectedPlan.currency}/min</InputAdornment> }}
                  inputProps={{ step: '0.0001' }} />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField fullWidth label="Sell Rate (Off-Peak)" type="number" value={rateForm.rate_offpeak}
                  onChange={e => setRateForm({ ...rateForm, rate_offpeak: e.target.value })}
                  InputProps={{ startAdornment: <InputAdornment position="start">{selectedPlan.currency}/min</InputAdornment> }}
                  inputProps={{ step: '0.0001' }} />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField fullWidth label="Buy Rate (Off-Peak)" type="number" value={rateForm.buy_rate_offpeak}
                  onChange={e => setRateForm({ ...rateForm, buy_rate_offpeak: e.target.value })}
                  InputProps={{ startAdornment: <InputAdornment position="start">{selectedPlan.currency}/min</InputAdornment> }}
                  inputProps={{ step: '0.0001' }} />
              </Grid>

              {/* Live margin indicator */}
              {rateForm.rate_peak !== '' && rateForm.buy_rate !== '' && (
                <Grid item xs={12}>
                  {marginInvalid() ? (
                    <Alert severity="error" icon={<Warning />}>
                      Sell rate is BELOW buy rate — this would create a negative margin loss. Increase sell rate above {rateForm.buy_rate}.
                    </Alert>
                  ) : sellBuyMargin() !== null ? (
                    <Alert severity={sellBuyMargin()! < 5 ? 'warning' : 'success'}>
                      Margin: {sellBuyMargin()!.toFixed(1)}% — Profit per minute: {selectedPlan.currency} {(parseFloat(rateForm.rate_peak) - parseFloat(rateForm.buy_rate)).toFixed(4)}
                    </Alert>
                  ) : null}
                </Grid>
              )}

              <Grid item xs={12}><Divider textAlign="left"><Typography variant="caption" color="text.secondary">BILLING SETTINGS</Typography></Divider></Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth type="number" label="Billing Increment (sec)" value={rateForm.billing_increment}
                  onChange={e => setRateForm({ ...rateForm, billing_increment: parseInt(e.target.value) || 6 })}
                  helperText="6 = 6-second billing, 1 = per-second, 60 = per-minute" />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth type="number" label="Min Duration (sec)" value={rateForm.min_duration}
                  onChange={e => setRateForm({ ...rateForm, min_duration: parseInt(e.target.value) || 0 })} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth type="number" label="Connection Fee" value={rateForm.connect_fee}
                  onChange={e => setRateForm({ ...rateForm, connect_fee: parseFloat(e.target.value) || 0 })}
                  InputProps={{ startAdornment: <InputAdornment position="start">{selectedPlan.currency}</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Effective Date" type="date" value={rateForm.effective_date}
                  onChange={e => setRateForm({ ...rateForm, effective_date: e.target.value })}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setRateDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={saveRate} disabled={saving || !rateForm.destination || marginInvalid()}>
              {saving ? <CircularProgress size={20} /> : editingRate ? 'Save Changes' : 'Add Rate'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* CSV Import Dialog */}
        <Dialog open={csvDialog} onClose={() => setCsvDialog(false)} maxWidth="lg" fullWidth>
          <DialogTitle>
            Import Rates from CSV
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              · ASTPP, Excel, or any CSV/TSV format · auto-detects delimiter &amp; column names
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {/* Import mode selector */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="body2" fontWeight={600}>Import mode:</Typography>
              <ToggleButtonGroup value={csvImportMode} exclusive size="small"
                onChange={(_, v) => { if (v) { setCsvImportMode(v); setCsvRows([]); setCsvFileName(''); setCsvMapping({}); setCsvWarnings([]); setCsvResult(null); } }}>
                <ToggleButton value="auto">
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="caption" fontWeight={700} display="block">Origination + Termination</Typography>
                    <Typography variant="caption" color="text.secondary">Both sell &amp; buy rates from file</Typography>
                  </Box>
                </ToggleButton>
                <ToggleButton value="termination">
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="caption" fontWeight={700} display="block">Termination Only</Typography>
                    <Typography variant="caption" color="text.secondary">Rate column → buy rate only (ASTPP export)</Typography>
                  </Box>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Format hint */}
            <Alert severity="info" icon={false} sx={{ mb: 2, py: 0.5 }}>
              <Typography variant="caption">
                {csvImportMode === 'termination' ? (
                  <><strong>ASTPP Termination Rates:</strong> Code → prefix · Destination → name · Cost/Min → buy rate · Increment → billing increment</>
                ) : (
                  <><strong>ASTPP:</strong> dialcode · origination_rate (sell) · termination_rate (buy) · increments &nbsp;|&nbsp; <strong>Generic:</strong> destination/prefix · sell_rate · buy_rate · billing_increment</>
                )}
                <br />Delimiter auto-detected · existing destinations <strong>updated</strong> · new ones <strong>created</strong>
              </Typography>
            </Alert>

            {/* File picker */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Button variant="outlined" component="label" startIcon={<Upload />}>
                Choose File (.csv / .txt / .tsv)
                <input type="file" accept=".csv,.txt,.tsv" hidden onChange={handleCsvFile} />
              </Button>
              {csvFileName && (
                <Chip label={csvFileName} size="small" variant="outlined" />
              )}
              {csvRows.length > 0 && (
                <Chip label={`${csvRows.length} rows parsed`} color="success" size="small" icon={<CheckCircle />} />
              )}
            </Box>

            {/* Warnings */}
            {csvWarnings.map((w, i) => (
              <Alert key={i} severity={w.startsWith('⚠') ? 'warning' : 'info'} sx={{ mb: 1, py: 0 }}>
                <Typography variant="caption">{w}</Typography>
              </Alert>
            ))}

            {/* Column mapping summary */}
            {Object.keys(csvMapping).length > 0 && (
              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  DETECTED COLUMN MAPPING
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {[
                    'Destination', 'Name', 'Sell Rate (Orig)', 'Buy Rate (Term)',
                    'Off-Peak Sell', 'Off-Peak Buy', 'Connect Fee', 'Billing Inc.'
                  ].map(field => {
                    const col = csvMapping[field];
                    return col ? (
                      <Chip key={field} size="small"
                        label={<><span style={{opacity:0.7}}>{field}: </span><strong>{col}</strong></>}
                        color={field.includes('Sell') || field.includes('Orig') ? 'success' : field.includes('Buy') || field.includes('Term') ? 'info' : 'default'}
                        variant="outlined" />
                    ) : (
                      <Chip key={field} size="small"
                        label={`${field}: not found`}
                        color={field === 'Destination' || field.includes('Sell') || field.includes('Buy') ? 'warning' : 'default'}
                        variant="outlined" sx={{ opacity: 0.5 }} />
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Import result */}
            {csvImporting && <LinearProgress sx={{ mb: 1 }} />}
            {csvResult && (
              <Alert severity={csvResult.errors.length > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
                <strong>Created: {csvResult.created}</strong> · <strong>Updated: {csvResult.updated}</strong>
                {csvResult.errors.length > 0 && (
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {csvResult.errors.slice(0, 8).map((e, i) => <li key={i}><Typography variant="caption">{e}</Typography></li>)}
                  </Box>
                )}
              </Alert>
            )}

            {/* Preview table */}
            {csvRows.length > 0 && (
              <Box sx={{ maxHeight: 320, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: 'background.paper' } }}>
                      <TableCell sx={{ fontWeight: 700 }}>Destination</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'success.main' }}>
                        Sell Rate
                        {csvMapping['Sell Rate (Orig)'] && (
                          <Typography variant="caption" display="block" color="text.secondary">← {csvMapping['Sell Rate (Orig)']}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'info.main' }}>
                        Buy Rate
                        {csvMapping['Buy Rate (Term)'] && (
                          <Typography variant="caption" display="block" color="text.secondary">← {csvMapping['Buy Rate (Term)']}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Off-Peak Sell</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Billing Inc.</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {csvRows.slice(0, 100).map((r, i) => {
                      const sell = parseFloat(r.rate_peak) || 0;
                      const buy  = parseFloat(r.buy_rate)  || 0;
                      const neg  = sell > 0 && buy > 0 && sell < buy;
                      return (
                        <TableRow key={i} sx={neg ? { bgcolor: 'error.lighter' } : {}}>
                          <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{r.destination}</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>{r.destination_name || '—'}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', color: 'success.main', fontWeight: 600 }}>
                            {r.rate_peak || '—'}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', color: r.buy_rate ? 'info.main' : 'text.disabled', fontWeight: r.buy_rate ? 600 : 400 }}>
                            {r.buy_rate || '—'}
                            {neg && <Typography variant="caption" color="error" sx={{ ml: 0.5 }}>⚠ loss</Typography>}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{r.rate_offpeak || '—'}</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>{r.billing_increment ? `${r.billing_increment}s` : '—'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {csvRows.length > 100 && (
                  <Typography variant="caption" sx={{ p: 1, display: 'block', color: 'text.secondary' }}>
                    Showing first 100 of {csvRows.length} rows
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setCsvDialog(false)}>Close</Button>
            <Button variant="contained" startIcon={csvImporting ? undefined : <Upload />} onClick={runCsvImport}
              disabled={csvRows.length === 0 || csvImporting || csvWarnings.some(w => w.includes('cannot import'))}>
              {csvImporting ? <><CircularProgress size={18} sx={{ mr: 1 }} />Importing...</> : `Import ${csvRows.length} Rates`}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Plans list view
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PriceChange sx={{ color: 'primary.main', fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Rate Plans</Typography>
            <Typography variant="body2" color="text.secondary">{totalPlans} plans — each rate has a destination code, trunk, sell rate and buy rate</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField size="small" placeholder="Search plans..." value={planSearch}
            onChange={e => { setPlanSearch(e.target.value); setPlanPage(0); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            sx={{ width: 220 }} />
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadPlans}>Refresh</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => { setEditingPlan(null); setPlanForm(emptyPlan); setError(''); setPlanDialog(true); }}>
            New Rate Plan
          </Button>
        </Box>
      </Box>

      <Tabs value={planTypeTab} onChange={(_, v) => { setPlanTypeTab(v); setPlanPage(0); }} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="all" label="All Plans" icon={<ListAlt fontSize="small" />} iconPosition="start" />
        <Tab value="retail" label="Customer Plans" icon={<People fontSize="small" />} iconPosition="start" />
        <Tab value="wholesale" label="Carrier Termination" icon={<PhoneForwarded fontSize="small" />} iconPosition="start" />
        <Tab value="reseller" label="Reseller Plans" icon={<ListAlt fontSize="small" />} iconPosition="start" />
      </Tabs>

      <Card>
        {loading ? <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box> : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Plan Name', 'Type', 'Currency', 'Billing Inc.', 'Status', 'Created', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {plans.map(plan => (
                  <TableRow key={plan.id} hover sx={{ cursor: 'pointer' }} onClick={() => openPlan(plan)}>
                    <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>{plan.name}</TableCell>
                    <TableCell>{planTypeChip(plan.plan_type)}</TableCell>
                    <TableCell>{plan.currency}</TableCell>
                    <TableCell>{plan.billing_increment}s</TableCell>
                    <TableCell><Chip label={plan.status} size="small" color={plan.status === 'active' ? 'success' : 'default'} /></TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{plan.created_at ? new Date(plan.created_at).toLocaleDateString() : '—'}</TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <Tooltip title="Edit Plan"><IconButton size="small" onClick={() => { setEditingPlan(plan); setPlanForm({ ...plan }); setError(''); setPlanDialog(true); }}><Edit fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete Plan"><IconButton size="small" color="error" onClick={async () => { if (window.confirm('Delete this rate plan and all its rates?')) { await api.delete(`/rates/plans/${plan.id}`); loadPlans(); } }}><Delete fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {plans.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>No rate plans yet. Create a rate plan to define pricing per destination.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination component="div" count={totalPlans} page={planPage} rowsPerPage={25}
              onPageChange={(_, p) => setPlanPage(p)} rowsPerPageOptions={[25]} />
          </>
        )}
      </Card>

      {/* Plan Dialog */}
      <Dialog open={planDialog} onClose={() => setPlanDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPlan ? 'Edit Rate Plan' : 'New Rate Plan'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Plan Name *" value={planForm.name} onChange={e => setPlanForm({ ...planForm, name: e.target.value })} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Description" value={planForm.description || ''} onChange={e => setPlanForm({ ...planForm, description: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth label="Plan Type" value={planForm.plan_type} onChange={e => setPlanForm({ ...planForm, plan_type: e.target.value })}>
                {['retail', 'wholesale', 'reseller', 'internal'].map(t => <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth label="Currency" value={planForm.currency} onChange={e => setPlanForm({ ...planForm, currency: e.target.value })}>
                {['USD', 'EUR', 'GBP', 'AED', 'SAR'].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth type="number" label="Default Billing Increment (sec)" value={planForm.billing_increment}
                onChange={e => setPlanForm({ ...planForm, billing_increment: parseInt(e.target.value) || 6 })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth type="number" label="DID monthly fee (USD per number)" value={planForm.did_monthly_fee ?? ''}
                inputProps={{ step: '0.01', min: 0 }} helperText="Added automatically to invoices for every assigned number. Empty = no DID charge."
                onChange={e => setPlanForm({ ...planForm, did_monthly_fee: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth label="Status" value={planForm.status} onChange={e => setPlanForm({ ...planForm, status: e.target.value })}>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPlanDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={savePlan} disabled={saving || !planForm.name}>
            {saving ? <CircularProgress size={20} /> : editingPlan ? 'Save Changes' : 'Create Plan'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
